import { Fragment, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/api/http";
import {
  robots,
  type RobotJournalEntry,
  type RobotRow,
} from "@/api/robots";
import { useToast } from "@/components/Toast";
import {
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  Input,
  Label,
  Select,
} from "@/components/ui";


const STRATEGY_TYPES = [
  "mean_reversion",
  "trend_follower",
  "breakout",
  "scalper",
  "grid",
  "multi_signal",
] as const;


function fmtErr(e: unknown): string {
  return e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e);
}


export function RobotsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const list = useQuery({
    queryKey: ["robots"],
    queryFn: () => robots.list(),
    refetchInterval: 5_000,
  });
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  function refresh() {
    qc.invalidateQueries({ queryKey: ["robots"] });
  }

  const start = useMutation({
    mutationFn: (id: string) => robots.start(id),
    onSuccess: () => { toast.push("Robot started", "success"); refresh(); },
    onError: (e) => toast.push(fmtErr(e), "error"),
  });
  const stop = useMutation({
    mutationFn: (id: string) => robots.stop(id),
    onSuccess: () => { toast.push("Robot stopped", "warn"); refresh(); },
    onError: (e) => toast.push(fmtErr(e), "error"),
  });
  const pause = useMutation({
    mutationFn: (id: string) => robots.pause(id),
    onSuccess: () => { toast.push("Robot paused", "warn"); refresh(); },
    onError: (e) => toast.push(fmtErr(e), "error"),
  });
  const resume = useMutation({
    mutationFn: (id: string) => robots.resume(id),
    onSuccess: () => { toast.push("Robot resumed", "success"); refresh(); },
    onError: (e) => toast.push(fmtErr(e), "error"),
  });
  const remove = useMutation({
    mutationFn: (id: string) => robots.delete(id),
    onSuccess: () => { toast.push("Robot deleted", "info"); refresh(); },
    onError: (e) => toast.push(fmtErr(e), "error"),
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-base font-semibold text-term-fg">Robots</h1>
          <p className="text-2xs text-term-mute">
            Long-running bots that read market bars and submit orders. Each
            robot belongs to your org; stops and pauses really halt event-bus
            subscriptions, and crashes surface as a red status with the
            stack trace.
          </p>
        </div>
        <Button onClick={() => setShowCreate((v) => !v)}>
          {showCreate ? "Hide form" : "Create robot"}
        </Button>
      </div>

      {showCreate && <CreateRobotForm onCreated={() => { setShowCreate(false); refresh(); }} />}

      <Card>
        <CardBody className="p-0">
          {list.isLoading ? (
            <p className="p-3 text-sm text-term-mute">Loading…</p>
          ) : !list.data || list.data.robots.length === 0 ? (
            <EmptyState
              title="No robots"
              description="Click 'Create robot' to add one. TOML configs in the default-org pool also show up here."
            />
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-term-elev text-term-mute">
                <tr>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">Name</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">Strategy</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">Status</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">Started</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase">Trades</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase">Signals</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {list.data.robots.map((r: RobotRow) => {
                  const expanded = expandedId === r.id;
                  return (
                    <Fragment key={r.id}>
                      <tr className="border-t border-term-border">
                        <td className="px-3 py-1.5 font-medium text-term-fg">{r.name}</td>
                        <td className="px-3 py-1.5 text-term-dim">{r.strategy}</td>
                        <td className="px-3 py-1.5">
                          <StatusBadge status={r.status} />
                          {r.last_error && (
                            <p className="mt-1 max-w-md text-2xs text-red-400" title={r.last_error_at ?? undefined}>
                              {r.last_error.length > 80 ? r.last_error.slice(0, 80) + "…" : r.last_error}
                            </p>
                          )}
                        </td>
                        <td className="px-3 py-1.5 text-term-dim">
                          {r.started_at ? new Date(r.started_at).toLocaleString() : "—"}
                        </td>
                        <td className="px-3 py-1.5 text-right text-term-fg">{r.trades}</td>
                        <td className="px-3 py-1.5 text-right text-term-dim">{r.signals}</td>
                        <td className="px-3 py-1.5 text-right">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" onClick={() => setExpandedId(expanded ? null : r.id)}>
                              {expanded ? "Hide" : "Details"}
                            </Button>
                            {r.status === "running" ? (
                              <>
                                <Button variant="secondary" onClick={() => pause.mutate(r.id)}>Pause</Button>
                                <Button variant="secondary" onClick={() => stop.mutate(r.id)}>Stop</Button>
                              </>
                            ) : r.status === "paused" ? (
                              <Button onClick={() => resume.mutate(r.id)}>Resume</Button>
                            ) : (
                              <Button onClick={() => start.mutate(r.id)}>Start</Button>
                            )}
                            <Button
                              variant="danger"
                              onClick={() => {
                                if (confirm(`Delete robot ${r.name}?`)) remove.mutate(r.id);
                              }}
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                      {expanded && (
                        <tr className="border-t border-term-border bg-term-elev/40">
                          <td colSpan={7} className="px-3 py-3">
                            <RobotDetail robotId={r.id} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}


function StatusBadge({ status }: { status: string }) {
  const tone =
    status === "running" ? "good"
    : status === "paused" ? "warn"
    : status === "error" ? "bad"
    : "neutral";
  return <Badge tone={tone}>{status}</Badge>;
}


function CreateRobotForm({ onCreated }: { onCreated: () => void }) {
  const toast = useToast();
  const [name, setName] = useState("");
  const [strategy, setStrategy] = useState<typeof STRATEGY_TYPES[number]>("mean_reversion");
  const [mode, setMode] = useState<"paper" | "live">("paper");
  const create = useMutation({
    mutationFn: () => robots.create({ name, strategy_type: strategy, mode }),
    onSuccess: () => {
      toast.push("Robot created", "success");
      setName("");
      onCreated();
    },
    onError: (e) => toast.push(fmtErr(e), "error"),
  });
  return (
    <Card>
      <CardBody className="space-y-2">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <div>
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="my-bot" />
          </div>
          <div>
            <Label>Strategy</Label>
            <Select value={strategy} onChange={(e) => setStrategy(e.target.value as typeof STRATEGY_TYPES[number])}>
              {STRATEGY_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
            </Select>
          </div>
          <div>
            <Label>Mode</Label>
            <Select value={mode} onChange={(e) => setMode(e.target.value as "paper" | "live")}>
              <option value="paper">Paper</option>
              <option value="live">Live</option>
            </Select>
          </div>
        </div>
        <Button
          onClick={() => {
            if (!name.trim()) return toast.push("Name is required", "error");
            create.mutate();
          }}
          loading={create.isPending}
        >
          Create
        </Button>
      </CardBody>
    </Card>
  );
}


function RobotDetail({ robotId }: { robotId: string }) {
  const trades = useQuery({
    queryKey: ["robot-trades", robotId],
    queryFn: () => robots.trades(robotId),
    refetchInterval: 15_000,
  });
  const signals = useQuery({
    queryKey: ["robot-signals", robotId],
    queryFn: () => robots.signals(robotId),
    refetchInterval: 15_000,
  });
  const errors = useQuery({
    queryKey: ["robot-errors", robotId],
    queryFn: () => robots.errors(robotId),
    refetchInterval: 30_000,
  });
  return (
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
      <JournalPanel
        title={`Signals (${signals.data?.signals.length ?? 0})`}
        rows={signals.data?.signals ?? []}
        loading={signals.isLoading}
      />
      <JournalPanel
        title={`Trades (${trades.data?.trades.length ?? 0})`}
        rows={trades.data?.trades ?? []}
        loading={trades.isLoading}
      />
      <JournalPanel
        title={`Errors (${errors.data?.errors.length ?? 0})`}
        rows={errors.data?.errors ?? []}
        loading={errors.isLoading}
        tone={errors.data && errors.data.errors.length > 0 ? "bad" : "neutral"}
      />
    </div>
  );
}


function JournalPanel({
  title, rows, loading, tone = "neutral",
}: {
  title: string;
  rows: RobotJournalEntry[];
  loading: boolean;
  tone?: "neutral" | "bad";
}) {
  const cls = tone === "bad" ? "text-red-400" : "text-term-mute";
  return (
    <div>
      <h3 className={`mb-1 text-2xs font-semibold uppercase ${cls}`}>{title}</h3>
      {loading ? (
        <p className="text-2xs text-term-mute">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-2xs text-term-mute">No entries.</p>
      ) : (
        <div className="max-h-64 overflow-y-auto">
          <table className="w-full text-2xs">
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-term-border/50">
                  <td className="px-1 py-0.5 text-term-dim whitespace-nowrap">
                    {new Date(r.ts).toLocaleTimeString()}
                  </td>
                  <td className="px-1 py-0.5 text-term-fg">{r.type}</td>
                  <td className="px-1 py-0.5 text-term-dim">{r.instrument}</td>
                  <td className="px-1 py-0.5 text-term-dim">{r.side}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
